/**
 * Minimal in-browser ZIP writer using the STORE method (no compression).
 *
 * JPEG / PNG screenshots are already entropy-compressed, so wrapping them
 * in a deflate stream would only save a few percent at the cost of pulling
 * in a compression dependency. STORE is what `zip -0` produces — a plain
 * container — and is enough for "Alle herunterladen".
 *
 * Format reference: PKWARE APPNOTE.TXT (.ZIP File Format Specification),
 * sections 4.3.6 (local header), 4.3.12 (central directory), 4.3.16 (EOCD).
 */

export interface ZipEntry {
  filename: string
  bytes: Uint8Array
}

export function makeZipBlob(entries: ZipEntry[]): Blob {
  const encoder = new TextEncoder()
  const chunks: Uint8Array[] = []
  const central: Uint8Array[] = []
  let runningOffset = 0

  for (const entry of entries) {
    const nameBytes = encoder.encode(entry.filename)
    const crc = crc32(entry.bytes)
    const size = entry.bytes.length

    // ── Local File Header (offset 0..29) + filename ──
    const local = new DataView(new ArrayBuffer(30 + nameBytes.length))
    local.setUint32(0, 0x04034b50, true)         // signature
    local.setUint16(4, 20, true)                  // version needed to extract
    local.setUint16(6, 0x0800, true)              // flags: UTF-8 filename
    local.setUint16(8, 0, true)                   // method: STORE
    local.setUint16(10, 0, true)                  // last mod time (midnight)
    local.setUint16(12, dosDate(), true)          // last mod date (today)
    local.setUint32(14, crc, true)                // CRC-32
    local.setUint32(18, size, true)               // compressed size
    local.setUint32(22, size, true)               // uncompressed size
    local.setUint16(26, nameBytes.length, true)
    local.setUint16(28, 0, true)                  // extra field length
    const localBytes = new Uint8Array(local.buffer)
    localBytes.set(nameBytes, 30)

    const entryOffset = runningOffset
    chunks.push(localBytes)
    chunks.push(entry.bytes)
    runningOffset += localBytes.length + entry.bytes.length

    // ── Central Directory entry for the same file ──
    const cd = new DataView(new ArrayBuffer(46 + nameBytes.length))
    cd.setUint32(0, 0x02014b50, true)             // signature
    cd.setUint16(4, 20, true)                     // version made by
    cd.setUint16(6, 20, true)                     // version needed
    cd.setUint16(8, 0x0800, true)                 // flags
    cd.setUint16(10, 0, true)                     // method
    cd.setUint16(12, 0, true)                     // time
    cd.setUint16(14, dosDate(), true)             // date
    cd.setUint32(16, crc, true)
    cd.setUint32(20, size, true)
    cd.setUint32(24, size, true)
    cd.setUint16(28, nameBytes.length, true)
    cd.setUint16(30, 0, true)                     // extra
    cd.setUint16(32, 0, true)                     // comment
    cd.setUint16(34, 0, true)                     // disk number
    cd.setUint16(36, 0, true)                     // internal attrs
    cd.setUint32(38, 0, true)                     // external attrs
    cd.setUint32(42, entryOffset, true)           // local header offset
    const cdBytes = new Uint8Array(cd.buffer)
    cdBytes.set(nameBytes, 46)
    central.push(cdBytes)
  }

  const centralOffset = runningOffset
  let centralSize = 0
  for (const ce of central) {
    chunks.push(ce)
    centralSize += ce.length
  }

  // ── End of Central Directory Record ──
  const eocd = new DataView(new ArrayBuffer(22))
  eocd.setUint32(0, 0x06054b50, true)
  eocd.setUint16(4, 0, true)                      // disk number
  eocd.setUint16(6, 0, true)                      // central dir start disk
  eocd.setUint16(8, entries.length, true)         // entries on this disk
  eocd.setUint16(10, entries.length, true)        // total entries
  eocd.setUint32(12, centralSize, true)           // central dir size
  eocd.setUint32(16, centralOffset, true)         // central dir offset
  eocd.setUint16(20, 0, true)                     // comment length
  chunks.push(new Uint8Array(eocd.buffer))

  return new Blob(chunks as BlobPart[], { type: 'application/zip' })
}

// ── CRC-32 (polynomial 0xEDB88320, IEEE 802.3 / PKZip) ────────────────────

let crcTable: Uint32Array | null = null

function getCrcTable(): Uint32Array {
  if (crcTable) return crcTable
  const t = new Uint32Array(256)
  for (let n = 0; n < 256; n++) {
    let c = n
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    t[n] = c >>> 0
  }
  crcTable = t
  return t
}

function crc32(bytes: Uint8Array): number {
  const t = getCrcTable()
  let c = 0xffffffff
  for (let i = 0; i < bytes.length; i++) {
    c = (t[(c ^ bytes[i]) & 0xff] ^ (c >>> 8)) >>> 0
  }
  return (c ^ 0xffffffff) >>> 0
}

function dosDate(): number {
  const d = new Date()
  return ((d.getFullYear() - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate()
}
